/**
 * 1.7.9 (D-1.7.9-9) — where a volunteer comes from: an optional Affiliation (one of the office's Organizations, or
 * "Other" with a typed name) and an optional group-or-class line ("Ms. Rivera's 11th grade").
 *
 * The list is one setting, VOLUNTEER_ORGANIZATIONS (Admin → Settings → Companion & Wellness → Volunteers and
 * outings → "Organizations"), kept like the events' lists: one line, a semicolon between the names. "Other" is
 * always offered. The whole answer lives in the volunteer's own file; the organization's name is also written to
 * the site's own column, SM_Volunteers.AffiliatedOrganization (Text). The group line has no column: the file only.
 *
 * It is a fact about a person and never a gate: nothing here is read by the Ready rules (shared/volunteers.ts),
 * and the VISIONS report does not carry it.
 */
import { z } from 'zod';
import { parseList } from './event-calendar';

export const VOLUNTEER_ORGS_KEY = 'VOLUNTEER_ORGANIZATIONS';
export const VOLUNTEER_ORGS_DEFAULT = 'LaSalle Academy; Student — school; VISIONS; Corporate group; Individual; Other';
/** The choice that takes a typed name. Always offered, whatever the list says. */
export const OTHER_ORG = 'Other';
/** How "not set" is written in an address (`?aff=none`) — never a list value. */
export const AFF_NONE = 'none';

export interface Affiliation {
  /** A value of the list, "Other", or '' (not set). */
  org: string;
  /** The typed name when `org` is "Other". */
  other: string;
  /** The group or class line. */
  group: string;
}

export const NO_AFFILIATION: Affiliation = { org: '', other: '', group: '' };

const text = (max: number) => z.string().trim().max(max).optional().default('');
/** The card's press and the sign-up's three answers. */
export const AffiliationSchema = z.object({ org: text(60), other: text(60), group: text(120) });
export type AffiliationInput = z.input<typeof AffiliationSchema>;

const clean = (v: unknown, max: number) => String(v ?? '').trim().replace(/\s+/g, ' ').slice(0, max);
const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();
const isOther = (v: string) => same(v, OTHER_ORG);

/** The list as the screens offer it: the setting's names in its order, "Other" last and always there. */
export function organizationsOf(value: string | null | undefined): string[] {
  const list = parseList(value, VOLUNTEER_ORGS_DEFAULT).filter((x) => !isOther(x));
  return [...list, OTHER_ORG];
}

/** True for a value the setting takes: at least one name, a line of at most 1500 characters. */
export const validOrganizations = (v: string) => v.length <= 1500 && parseList(v, '').length >= 1;

/**
 * The answer as it stands against the list today: a name on the list is a list value (whether it was chosen or
 * typed under Other — so a promoted name reads as a list value on every card); a name that is not on the list
 * (typed, or a list value the office later took off) is "Other" with that name. The group line is kept as written.
 */
export function settleAffiliation(a: Partial<Affiliation> | null | undefined, orgs: readonly string[]): Affiliation {
  let org = clean(a?.org, 60);
  let other = clean(a?.other, 60);
  const group = clean(a?.group, 120);
  const listed = (x: string) => orgs.find((o) => !isOther(o) && same(o, x));
  if (org && !isOther(org)) {
    const hit = listed(org);
    if (hit) return { org: hit, other: '', group };
    other = other || org;
    org = OTHER_ORG;
  }
  if (isOther(org) || other) {
    const hit = other ? listed(other) : undefined;
    if (hit) return { org: hit, other: '', group };
    return { org: OTHER_ORG, other, group };
  }
  return { org: '', other: '', group };
}

/** The organization in words: the list value, the typed name, "Other" when nothing was typed, '' when not set. */
export function affiliationLabel(a: Pick<Affiliation, 'org' | 'other'> | null | undefined): string {
  if (!a || !a.org) return '';
  return isOther(a.org) ? a.other || OTHER_ORG : a.org;
}

/** Is anything recorded (an organization or a group line)? */
export const hasAffiliation = (a: Affiliation | null | undefined): boolean => !!a && !!(a.org || a.other || a.group);

/** May the typed name be promoted to the list: "Other" with a name that is not on the list yet. */
export function canPromote(a: Affiliation | null | undefined, orgs: readonly string[]): boolean {
  if (!a || !isOther(a.org) || !a.other) return false;
  return !orgs.some((o) => same(o, a.other));
}

/** The setting's value with one more name, put before "Other" (which stays last). Null when it is on the list already or the line would be too long. */
export function withOrganization(value: string | null | undefined, name: string): string | null {
  const add = clean(name, 60);
  const list = parseList(value, VOLUNTEER_ORGS_DEFAULT);
  if (!add || add.includes(';') || list.some((o) => same(o, add))) return null;
  const rest = list.filter((x) => !isOther(x));
  const next = [...rest, add, ...(list.length !== rest.length ? [OTHER_ORG] : [])].join('; ');
  return validOrganizations(next) && parseList(next, '').length === list.length + 1 ? next : null;
}

export interface AffiliationCount {
  /** A list value, "Other", or '' for not set. */
  org: string;
  count: number;
}

/**
 * How many volunteers under each organization: the list's names in the list's order (only those somebody has),
 * then "Other" (every typed name together), then not set — always last, always shown.
 */
export function affiliationCounts(rows: readonly { affiliationOrg?: string }[], orgs: readonly string[]): AffiliationCount[] {
  const n = new Map<string, number>();
  for (const r of rows) n.set(r.affiliationOrg ?? '', (n.get(r.affiliationOrg ?? '') ?? 0) + 1);
  const out: AffiliationCount[] = [];
  for (const o of orgs) if (n.get(o)) out.push({ org: o, count: n.get(o)! });
  // a value the list no longer has (the row was settled against an older list): still counted, under its own name
  for (const [o, count] of n) if (o && !orgs.includes(o)) out.push({ org: o, count });
  out.push({ org: '', count: n.get('') ?? 0 });
  return out;
}

/** Does a volunteer's row pass the list's filter (`null` = no filter; AFF_NONE = not set)? */
export function matchesAffiliation(row: { affiliationOrg?: string }, filter: string | null): boolean {
  if (!filter) return true;
  const org = row.affiliationOrg ?? '';
  return filter === AFF_NONE ? org === '' : org === filter;
}

/** What changed, in the audit's words ("LaSalle Academy → VISIONS", "not set → Other: Hunter College"). */
export function affiliationWords(a: Affiliation | null | undefined): string {
  if (!hasAffiliation(a)) return 'not set';
  const label = a!.org ? (isOther(a!.org) ? `Other${a!.other ? `: ${a!.other}` : ''}` : a!.org) : 'no organization';
  return a!.group ? `${label} (${a!.group})` : label;
}

export const sameAffiliation = (a: Affiliation | null | undefined, b: Affiliation | null | undefined): boolean => {
  const x = a ?? NO_AFFILIATION;
  const y = b ?? NO_AFFILIATION;
  return x.org === y.org && x.other === y.other && x.group === y.group;
};
